import { createSlice } from "@reduxjs/toolkit";
import {
  deleteStory,
  fetchStoryTray,
  fetchStoryViewers,
  reactToStory,
  registerStoryView,
} from "../actions/storiesActions";

const initialState = {
  tray: {
    data: [],
    isLoading: false,
    error: null,
    lastFetchedAt: null,
    // How long a Storia's clip may run, straight from the backend .env. The
    // fallback matches the server's own default, so the composer still works
    // if the tray has not been fetched yet.
    maxVideoSeconds: 60,
    // The upload ceiling in MB, also from the backend. A minute of 4K video is
    // far more than this, so the composer has to say so before uploading.
    maxMediaMb: 120,
  },
  // The automatic-Story proposal in flight, if any. Kept here rather than in
  // the screen that triggered it: saving a measure closes its own screen, so a
  // locally-owned prompt unmounted before anyone could read it.
  prompt: null,
  viewers: {
    storyId: null,
    users: [],
    count: 0,
    isLoading: false,
    error: null,
  },
};

/** Applies `mutate` to one story wherever it sits in the tray. */
const eachStory = (state, storyId, mutate) => {
  state.tray.data.forEach((group) => {
    group.stories.forEach((story) => {
      if (String(story.id) === String(storyId)) mutate(story, group);
    });
  });
};

const storiesSlice = createSlice({
  name: "stories",
  initialState,
  reducers: {
    /**
     * Marks a story seen locally the moment it opens, so the ring and the
     * unseen-first ordering settle without waiting for the round trip.
     */
    markStorySeen: (state, action) => {
      const storyId = action.payload;
      eachStory(state, storyId, (story) => {
        story.attributes.seen = true;
      });
      state.tray.data.forEach((group) => {
        group.has_unseen =
          !group.is_own && group.stories.some((s) => !s.attributes.seen);
      });
    },
    setStoryPrompt: (state, action) => {
      state.prompt = action.payload;
    },
    clearStoryPrompt: (state) => {
      state.prompt = null;
    },
    clearStoryViewers: (state) => {
      state.viewers = initialState.viewers;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchStoryTray.pending, (state) => {
      state.tray.isLoading = true;
      state.tray.error = null;
    });
    builder.addCase(fetchStoryTray.fulfilled, (state, action) => {
      state.tray.isLoading = false;
      state.tray.data = action.payload?.groups ?? [];
      const max = Number(action.payload?.meta?.max_video_seconds);
      if (Number.isFinite(max) && max > 0) state.tray.maxVideoSeconds = max;
      const mb = Number(action.payload?.meta?.max_media_mb);
      if (Number.isFinite(mb) && mb > 0) state.tray.maxMediaMb = mb;
      state.tray.lastFetchedAt = Date.now();
    });
    builder.addCase(fetchStoryTray.rejected, (state, action) => {
      state.tray.isLoading = false;
      state.tray.error = action.payload || action.error?.message || null;
    });

    builder.addCase(registerStoryView.fulfilled, (state, action) => {
      const { storyId, views_count: viewsCount } = action.payload;
      eachStory(state, storyId, (story) => {
        story.attributes.seen = true;
        if (story.attributes.views_count !== null) {
          story.attributes.views_count = viewsCount;
        }
      });
    });

    builder.addCase(reactToStory.fulfilled, (state, action) => {
      const { storyId, my_reaction: myReaction } = action.payload;
      eachStory(state, storyId, (story) => {
        story.attributes.my_reaction = myReaction;
      });
    });

    builder.addCase(deleteStory.fulfilled, (state, action) => {
      const { storyId } = action.payload;
      state.tray.data = state.tray.data
        .map((group) => ({
          ...group,
          stories: group.stories.filter(
            (story) => String(story.id) !== String(storyId),
          ),
        }))
        .filter((group) => group.stories.length > 0);
    });

    builder.addCase(fetchStoryViewers.pending, (state, action) => {
      state.viewers.isLoading = true;
      state.viewers.error = null;
      state.viewers.storyId = action.meta.arg?.storyId ?? null;
    });
    builder.addCase(fetchStoryViewers.fulfilled, (state, action) => {
      state.viewers.isLoading = false;
      state.viewers.storyId = action.payload.storyId;
      state.viewers.users = action.payload.users ?? [];
      state.viewers.count = action.payload.views_count ?? 0;
    });
    builder.addCase(fetchStoryViewers.rejected, (state, action) => {
      state.viewers.isLoading = false;
      state.viewers.error = action.payload || action.error?.message || null;
    });
  },
});

export const {
  markStorySeen,
  setStoryPrompt,
  clearStoryPrompt,
  clearStoryViewers,
} = storiesSlice.actions;

export default storiesSlice.reducer;
